import { create } from 'zustand'
import {
  getSyncKey,
  setSyncKey,
  clearSyncKey,
  fetchSyncedData,
  saveSyncedData,
  getSyncUrl,
  connectWebSocket,
  type SyncStatus,
} from './lib/sync'
import { useCrosswordStore } from './store'
import type { CrosswordData } from './types'
import {
  areCrosswordDataEquivalent,
  getCrosswordDataDifferences,
  getCrosswordDataChangeDetails,
  getCrosswordDataFingerprint,
  hasMeaningfulCrosswordData,
  inferCrosswordDataUpdatedAt,
  summarizeCrosswordData,
  type DataChangeDetail,
  type DataDifference,
  type DataSummary,
} from './lib/data'

type SyncConflict = {
  remoteData: CrosswordData
  localSummary: DataSummary
  remoteSummary: DataSummary
  differences: DataDifference[]
  details: DataChangeDetail[]
  hasContentChanges: boolean
  hasMetadataOnlyChanges: boolean
  localUpdatedAt: string | null
  remoteUpdatedAt: string | null
  remoteClientId: string | null
}

type SyncStore = {
  syncKey: string | null
  status: SyncStatus
  lastSyncedAt: string | null
  error: string | null
  conflict: SyncConflict | null
  isConnected: boolean

  // Actions
  initialize: () => Promise<void>
  setSyncKey: (key: string) => Promise<void>
  clearSync: () => void
  pushToRemote: () => Promise<void>
  pullFromRemote: () => Promise<void>
  getSyncUrl: () => string | null

  // Conflict resolution
  resolveConflict: (choice: 'local' | 'remote' | 'merge') => Promise<void>
  cancelConflict: () => void
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null
let autoSyncSetup = false
let wsConnection: { close: () => void } | null = null

// Track when we're pulling to prevent sync loop
let isPulling = false

// Track last pushed data to avoid re-pushing identical data
let lastPushedDataSnapshot: string | null = null

let lastLocalChangedAt: string | null = null
let pendingRemotePull = false

function serializeData(data: CrosswordData): string {
  return getCrosswordDataFingerprint(data)
}

function dataEquals(a: CrosswordData, b: CrosswordData): boolean {
  return areCrosswordDataEquivalent(a, b)
}

function buildConflict(
  localData: CrosswordData,
  remoteData: CrosswordData,
  metadata: { updatedAt: string | null; clientId: string | null }
): SyncConflict {
  const details = getCrosswordDataChangeDetails(localData, remoteData)
  const hasContentChanges = details.some(detail => detail.kind === 'content')
  return {
    remoteData,
    localSummary: summarizeCrosswordData(localData),
    remoteSummary: summarizeCrosswordData(remoteData),
    differences: getCrosswordDataDifferences(localData, remoteData),
    details,
    hasContentChanges,
    hasMetadataOnlyChanges: details.length > 0 && !hasContentChanges,
    localUpdatedAt: lastLocalChangedAt ?? inferCrosswordDataUpdatedAt(localData),
    remoteUpdatedAt: metadata.updatedAt,
    remoteClientId: metadata.clientId,
  }
}

function resetPendingSyncState() {
  if (debounceTimer) {
    clearTimeout(debounceTimer)
    debounceTimer = null
  }
  pendingRemotePull = false
  lastLocalChangedAt = null
  lastPushedDataSnapshot = null
}

function setupWebSocket(key: string) {
  // Close existing connection
  if (wsConnection) {
    wsConnection.close()
    wsConnection = null
  }

  wsConnection = connectWebSocket(
    key,
    // onUpdate - pull when notified of changes
    () => {
      useSyncStore.getState().pullFromRemote()
    },
    // onConnected
    () => {
      useSyncStore.setState({ isConnected: true })
    }
  )
}

export const useSyncStore = create<SyncStore>((set, get) => ({
  syncKey: null,
  status: 'idle',
  lastSyncedAt: null,
  error: null,
  conflict: null,
  isConnected: false,

  initialize: async () => {
    const key = getSyncKey()
    if (!key) {
      set({ syncKey: null, status: 'idle' })
      return
    }

    set({ syncKey: key, status: 'syncing', error: null })

    const result = await fetchSyncedData(key)
    if (!result.success) {
      set({ status: 'error', error: result.error })
      return
    }

    const localData = useCrosswordStore.getState().exportData()
    const hasLocalData = hasMeaningfulCrosswordData(localData)
    let syncedAt = result.updatedAt

    if (result.data && hasLocalData) {
      // Both have data - check if they're the same
      if (!dataEquals(localData, result.data)) {
        // Different data - show conflict
        set({
          status: 'idle',
          conflict: buildConflict(localData, result.data, result),
        })
        return
      }
      // Same data - no conflict, just continue
      lastPushedDataSnapshot = serializeData(localData)
    } else if (result.data) {
      // Only remote has data - use it
      useCrosswordStore.getState().importData(result.data)
      lastPushedDataSnapshot = serializeData(result.data)
    } else if (hasLocalData) {
      // Only local has data - push it
      const saveResult = await saveSyncedData(key, localData)
      if (!saveResult.success) {
        set({ status: 'error', error: saveResult.error })
        return
      }
      lastPushedDataSnapshot = serializeData(localData)
      syncedAt = saveResult.updatedAt
    } else {
      lastPushedDataSnapshot = serializeData(localData)
    }

    set({
      status: 'synced',
      lastSyncedAt: syncedAt ?? new Date().toISOString(),
    })

    setupAutoSync()
    setupWebSocket(key)
  },

  setSyncKey: async (key) => {
    setSyncKey(key)
    set({ syncKey: key, status: 'syncing', error: null, conflict: null })

    const result = await fetchSyncedData(key)
    if (!result.success) {
      set({ status: 'error', error: result.error })
      return
    }

    const localData = useCrosswordStore.getState().exportData()
    const hasLocalData = hasMeaningfulCrosswordData(localData)
    let syncedAt = result.updatedAt

    if (result.data && hasLocalData) {
      // Both have data - check if they're the same
      if (!dataEquals(localData, result.data)) {
        // Different data - show conflict
        set({
          status: 'idle',
          conflict: buildConflict(localData, result.data, result),
        })
        return
      }
      // Same data - no conflict, just continue
      lastPushedDataSnapshot = serializeData(localData)
    } else if (result.data) {
      // Only remote has data - use it
      useCrosswordStore.getState().importData(result.data)
      lastPushedDataSnapshot = serializeData(result.data)
    } else if (hasLocalData) {
      // Only local has data - push it
      const saveResult = await saveSyncedData(key, localData)
      if (!saveResult.success) {
        set({ status: 'error', error: saveResult.error })
        return
      }
      lastPushedDataSnapshot = serializeData(localData)
      syncedAt = saveResult.updatedAt
    } else {
      lastPushedDataSnapshot = serializeData(localData)
    }

    set({
      status: 'synced',
      lastSyncedAt: syncedAt ?? new Date().toISOString(),
    })

    setupAutoSync()
    setupWebSocket(key)
  },

  resolveConflict: async (choice) => {
    const { syncKey, conflict } = get()
    if (!syncKey || !conflict) return

    set({ status: 'syncing', conflict: null })

    if (choice === 'remote') {
      useCrosswordStore.getState().importData(conflict.remoteData)
    } else if (choice === 'merge') {
      useCrosswordStore.getState().mergeData(conflict.remoteData)
    }
    // 'local' - keep current data, just push

    // Push current state to remote
    const data = useCrosswordStore.getState().exportData()
    const result = await saveSyncedData(syncKey, data)
    if (!result.success) {
      set({ status: 'error', error: result.error })
      return
    }

    lastPushedDataSnapshot = serializeData(data)
    lastLocalChangedAt = null
    pendingRemotePull = false

    set({
      status: 'synced',
      lastSyncedAt: result.updatedAt,
    })

    setupAutoSync()
    setupWebSocket(syncKey)
  },

  cancelConflict: () => {
    if (wsConnection) {
      wsConnection.close()
      wsConnection = null
    }
    resetPendingSyncState()
    clearSyncKey()
    set({
      syncKey: null,
      status: 'idle',
      conflict: null,
      isConnected: false,
    })
  },

  clearSync: () => {
    if (wsConnection) {
      wsConnection.close()
      wsConnection = null
    }
    resetPendingSyncState()
    clearSyncKey()
    set({
      syncKey: null,
      status: 'idle',
      lastSyncedAt: null,
      error: null,
      conflict: null,
      isConnected: false,
    })
  },

  pushToRemote: async () => {
    const { syncKey } = get()
    if (!syncKey) return

    set({ status: 'syncing', error: null })

    const data = useCrosswordStore.getState().exportData()
    const result = await saveSyncedData(syncKey, data)

    if (!result.success) {
      set({ status: 'error', error: result.error })
      return
    }

    // Update hash to track what we pushed
    lastPushedDataSnapshot = serializeData(data)
    lastLocalChangedAt = null

    set({
      status: 'synced',
      lastSyncedAt: result.updatedAt,
    })

    if (pendingRemotePull) {
      pendingRemotePull = false
      void get().pullFromRemote()
    }
  },

  pullFromRemote: async () => {
    const { syncKey } = get()
    if (!syncKey) return

    // Skip pull if we have pending local changes (debounce timer active)
    // This prevents overwriting local edits that haven't been pushed yet
    if (debounceTimer) {
      pendingRemotePull = true
      return
    }

    set({ status: 'syncing', error: null })

    const result = await fetchSyncedData(syncKey)
    if (!result.success) {
      set({ status: 'error', error: result.error })
      return
    }

    if (result.data) {
      const localData = useCrosswordStore.getState().exportData()
      const localHash = serializeData(localData)
      const hasUnsyncedLocalData = hasMeaningfulCrosswordData(localData)
        && lastPushedDataSnapshot !== null
        && localHash !== lastPushedDataSnapshot

      if (hasUnsyncedLocalData && !dataEquals(localData, result.data)) {
        set({
          status: 'idle',
          conflict: buildConflict(localData, result.data, result),
        })
        return
      }

      // Set flag to prevent auto-sync from triggering during import
      isPulling = true
      try {
        // Update the hash so we don't re-push identical data
        lastPushedDataSnapshot = serializeData(result.data)
        lastLocalChangedAt = null
        useCrosswordStore.getState().importData(result.data)
      } finally {
        isPulling = false
      }
    }

    set({
      status: 'synced',
      lastSyncedAt: result.updatedAt ?? new Date().toISOString(),
    })
  },

  getSyncUrl: () => {
    const { syncKey } = get()
    if (!syncKey) return null
    return getSyncUrl(syncKey)
  },
}))

function setupAutoSync() {
  if (autoSyncSetup) return
  autoSyncSetup = true

  useCrosswordStore.subscribe(() => {
    const { syncKey, status, conflict } = useSyncStore.getState()
    if (!syncKey || status === 'syncing' || conflict) return

    // Skip if we're currently pulling (prevents sync loop)
    if (isPulling) return

    // Skip if a drag is in progress (draggedPairingId is set)
    const { draggedPairingId } = useCrosswordStore.getState()
    if (draggedPairingId) return

    if (debounceTimer) {
      clearTimeout(debounceTimer)
    }

    debounceTimer = setTimeout(() => {
      debounceTimer = null

      // Before pushing, check if data has actually changed
      const data = useCrosswordStore.getState().exportData()
      const currentHash = serializeData(data)
      if (currentHash === lastPushedDataSnapshot) {
        // Data is identical to what we last pushed/pulled, skip
        return
      }

      lastLocalChangedAt = inferCrosswordDataUpdatedAt(data) ?? new Date().toISOString()
      if (pendingRemotePull) {
        pendingRemotePull = false
        void useSyncStore.getState().pullFromRemote()
        return
      }

      void useSyncStore.getState().pushToRemote()
    }, 1000)
  })
}
